import type {
  MembershipPlan,
  MembershipPlanDiscount,
  MemberPlanCatalogEntry,
  MemberPlanOffer,
} from '../../api/payments';

export interface SubUserFormData {
  firstName: string;
  lastName: string;
  dob: Date | null;
  weightKg: number | null;
  heightCm: number | null;
  measurementUnit: 'metric' | 'imperial';
}

/** Deterministic dummy UUID prefix for unsaved pending sub-users. */
const DUMMY_UUID_PREFIX = '00000000-0000-0000-0000-';

/** Check whether an ID is a deterministic dummy UUID used for pending members. */
export function isDummyUUID(id: string | null | undefined): boolean {
  return !!id && id.startsWith(DUMMY_UUID_PREFIX);
}

/** Generate the next sequential deterministic dummy UUID given existing members. */
export function getNextDummyUUID(existingIds: (string | null | undefined)[]): string {
  let maxIndex = 0;
  for (const id of existingIds) {
    if (isDummyUUID(id)) {
      const suffix = id!.slice(DUMMY_UUID_PREFIX.length);
      const num = parseInt(suffix, 10);
      if (!isNaN(num) && num > maxIndex) maxIndex = num;
    }
  }
  return `${DUMMY_UUID_PREFIX}${String(maxIndex + 1).padStart(12, '0')}`;
}

export const MEMBERSHIP_TERM_OPTIONS = [
  { months: 1, sortOrder: 1 },
  { months: 3, sortOrder: 2 },
  { months: 6, sortOrder: 3 },
  { months: 12, sortOrder: 4 },
] as const;

export type MembershipTermMonths = typeof MEMBERSHIP_TERM_OPTIONS[number]['months'];

export function isSupportedMembershipTerm(termMonths: number): termMonths is MembershipTermMonths {
  return MEMBERSHIP_TERM_OPTIONS.some((option) => option.months === termMonths);
}

export function calcAge(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

export function getFilteredPlans(plans: MembershipPlan[] | undefined, dob: string | null): MembershipPlan[] {
  const age = calcAge(dob);
  return (plans || []).filter((p) => {
    if (age === null) return true;
    if (p.is_kids && age > 15) return false;
    if (!p.is_kids && age >= 5 && age <= 15) return false;
    return true;
  });
}

export function getSortedPlans(
  plans: MembershipPlan[] | undefined,
  dob: string | null,
): { plan: MembershipPlan; slug: string }[] {
  return getFilteredPlans(plans, dob)
    .map((plan) => {
      const slug = plan.machine_name;
      return { plan, slug };
    })
    .sort((a, b) => a.plan.sort_order - b.plan.sort_order);
}

export function getPlanTermDiscount(plan: MembershipPlan, termMonths: number) {
  return plan.discounts?.find((discount) => discount.term_months === termMonths && discount.is_active) || null;
}

/**
 * Returns a smart discount description string based on discount type.
 * Pass a `t` function for i18n; falls back to English-style strings if omitted.
 * formatCurrencyFn is required for amount/final_amount types.
 */
export function getDiscountText(
  discount: MembershipPlanDiscount,
  subtotal: number,
  finalAmount: number,
  formatCurrencyFn: (amount: number) => string,
  t?: (key: string, opts?: Record<string, unknown>) => string,
  termMonths?: number,
): string {
  const tr = t ?? ((key: string, opts?: Record<string, unknown>) => {
    if (key === 'billing.discountPercent') return `${opts?.percent}% off`;
    if (key === 'billing.discountFreeMonths') return `${opts?.count} month${Number(opts?.count) !== 1 ? 's' : ''} free`;
    if (key === 'billing.saveAmount') return `Save ${opts?.amount} every ${opts?.count} months`;
    return String(opts?.amount ?? '');
  });

  const savedAmount = Math.max(0, subtotal - finalAmount);

  if (discount.final_amount !== null) {
    return tr('billing.saveAmount', { amount: formatCurrencyFn(savedAmount), count: termMonths ?? 1 });
  }

  // Only show discount text based on discount_type
  if (discount.discount_type === 'percent_discount' && discount.percent_discount !== null) {
    const pct = parseFloat(discount.percent_discount);
    if (pct > 0) return tr('billing.discountPercent', { percent: pct });
  }
  if (discount.discount_type === 'free_months' && discount.free_months !== null && discount.free_months > 0) {
    return tr('billing.discountFreeMonths', { count: discount.free_months });
  }
  if (discount.discount_type === 'amount_discount' && discount.amount_discount !== null && parseFloat(discount.amount_discount) > 0) {
    return tr('billing.saveAmount', { amount: formatCurrencyFn(savedAmount), count: termMonths ?? 1 });
  }
  if (savedAmount > 0) return tr('billing.saveAmount', { amount: formatCurrencyFn(savedAmount), count: termMonths ?? 1 });
  return '';
}

export function calculatePlanTermPrice(plan: MembershipPlan, termMonths: number): {
  subtotal: number;
  discountAmount: number;
  finalAmount: number;
  discountLabel: string | null;
  discount: MembershipPlanDiscount | null;
} {
  const monthlyPrice = parseFloat(plan.price || '0');
  const subtotal = monthlyPrice * termMonths;
  const discount = getPlanTermDiscount(plan, termMonths);

  if (!discount) {
    return { subtotal, discountAmount: 0, finalAmount: subtotal, discountLabel: null, discount: null };
  }

  if (discount.final_amount !== null) {
    const finalAmount = parseFloat(discount.final_amount);
    return {
      subtotal,
      discountAmount: Math.max(0, subtotal - finalAmount),
      finalAmount,
      discountLabel: discount.label,
      discount,
    };
  }

  let discountAmount = 0;
  // Only apply the discount based on discount_type
  if (discount.discount_type === 'percent_discount' && discount.percent_discount !== null) {
    discountAmount = subtotal * (parseFloat(discount.percent_discount) / 100);
  } else if (discount.discount_type === 'amount_discount' && discount.amount_discount !== null) {
    discountAmount = parseFloat(discount.amount_discount);
  } else if (discount.discount_type === 'free_months' && discount.free_months !== null && discount.free_months > 0) {
    discountAmount = monthlyPrice * discount.free_months;
  }

  discountAmount = Math.min(discountAmount, subtotal);
  return {
    subtotal,
    discountAmount,
    finalAmount: Math.max(0, subtotal - discountAmount),
    discountLabel: discount.label,
    discount,
  };
}

// ---------------------------------------------------------------------------
// Catalog-aware helpers (new discount system)
// ---------------------------------------------------------------------------

export type { MemberPlanCatalogEntry, MemberPlanOffer };

/**
 * Look up a member's catalog entry by user_id from the catalog response entries array.
 */
export function getMemberCatalogEntry(
  entries: MemberPlanCatalogEntry[],
  userId: string,
): MemberPlanCatalogEntry | null {
  return entries.find((e) => e.user_id === userId) ?? null;
}

/**
 * Get all offers for a member from the catalog, optionally filtered to active-only.
 */
export function getMemberOffers(
  entries: MemberPlanCatalogEntry[],
  userId: string,
): MemberPlanOffer[] {
  return getMemberCatalogEntry(entries, userId)?.offers ?? [];
}

/**
 * Returns true if the given plan is being shown to the member as a
 * targeted/exclusive offer (i.e. a hidden plan unlocked for them, or a plan
 * carrying a `targeted` discount source). Used to badge the plan card so the
 * member understands this offer is specifically for them.
 */
export function isTargetedOfferForPlan(
  offers: MemberPlanOffer[] | undefined,
  planId: string,
): boolean {
  if (!offers) return false;
  return offers.some(
    (o) =>
      o.plan_id === planId &&
      (o.is_exclusive || o.discount_source === 'targeted'),
  );
}

/**
 * Find the best offer for a specific plan+term combination for a member.
 * Prefers targeted/dependent offers over public ones.
 */
export function getOfferForPlanTerm(
  offers: MemberPlanOffer[],
  planId: string | null,
  termMonths: number,
): MemberPlanOffer | null {
  if (!planId) return null;
  const matching = offers.filter(
    (o) => o.plan_id === planId && o.allowed_term_months.includes(termMonths),
  );
  if (!matching.length) return null;
  // Prefer targeted > dependent > public (has discount with source)
  return (
    matching.find((o) => o.discount_source === 'targeted') ??
    matching.find((o) => o.discount_source === 'dependent') ??
    matching[0]
  );
}

/**
 * Compute pricing for a member's plan+term using catalog offer data.
 * Falls back to `calculatePlanTermPrice` if no catalog offer is available.
 */
export function calculateOfferPrice(
  plan: MembershipPlan,
  termMonths: number,
  offer: MemberPlanOffer | null,
): {
  subtotal: number;
  discountAmount: number;
  finalAmount: number;
  discountLabel: string | null;
  discount: MembershipPlanDiscount | null;
} {
  // Same order as checkout: the targeted offer priced for this term, else the member's
  // dependent tier, else public pricing. A targeted offer for another term is skipped.
  const targeted = (offer?.targeted_discounts ?? []).find((d) => discountAppliesToTerm(d, termMonths));
  if (targeted) {
    return calculatePlanTermPriceWithDiscount(plan, termMonths, targeted);
  }
  if (offer?.discount && discountAppliesToTerm(offer.discount, termMonths)) {
    return calculatePlanTermPriceWithDiscount(plan, termMonths, offer.discount);
  }
  if (offer?.tier_discount && discountAppliesToTerm(offer.tier_discount, termMonths)) {
    return calculatePlanTermPriceWithDiscount(plan, termMonths, offer.tier_discount);
  }
  return calculatePlanTermPrice(plan, termMonths);
}

/**
 * A discount priced for one term (term_months set: targeted offers) only applies
 * when checking out for exactly that term; term-less discounts (dependent tiers)
 * always apply. Mirrors the server's discount_applies_to_term.
 */
export function discountAppliesToTerm(
  discount: MembershipPlanDiscount,
  termMonths: number,
): boolean {
  return discount.term_months == null || discount.term_months === termMonths;
}

/**
 * Compute pricing for a plan+term using a specific discount (bypasses the
 * plan's public discount list — for targeted / dependent offers).
 */
export function calculatePlanTermPriceWithDiscount(
  plan: MembershipPlan,
  termMonths: number,
  discount: MembershipPlanDiscount,
): {
  subtotal: number;
  discountAmount: number;
  finalAmount: number;
  discountLabel: string | null;
  discount: MembershipPlanDiscount;
} {
  const monthlyPrice = parseFloat(plan.price || '0');
  const subtotal = monthlyPrice * termMonths;

  if (discount.final_amount !== null) {
    const finalAmount = parseFloat(discount.final_amount);
    return {
      subtotal,
      discountAmount: Math.max(0, subtotal - finalAmount),
      finalAmount,
      discountLabel: discount.label,
      discount,
    };
  }

  let discountAmount = 0;
  if (discount.discount_type === 'percent_discount' && discount.percent_discount !== null) {
    discountAmount = subtotal * (parseFloat(discount.percent_discount) / 100);
  } else if (discount.discount_type === 'amount_discount' && discount.amount_discount !== null) {
    discountAmount = parseFloat(discount.amount_discount);
  } else if (discount.discount_type === 'free_months' && discount.free_months !== null && discount.free_months > 0) {
    discountAmount = monthlyPrice * discount.free_months;
  }

  discountAmount = Math.min(discountAmount, subtotal);
  return {
    subtotal,
    discountAmount,
    finalAmount: Math.max(0, subtotal - discountAmount),
    discountLabel: discount.label,
    discount,
  };
}

/**
 * Returns the allowed term months for a member on a given plan, from their
 * catalog offers.  Falls back to `MEMBERSHIP_TERM_OPTIONS` if no catalog data.
 */
export function getAllowedTermsForMemberPlan(
  offers: MemberPlanOffer[],
  planId: string | null,
): number[] {
  if (!planId) return [3, 6, 12];
  // Collect all allowed terms from all offers for this plan
  const terms = new Set<number>();
  for (const offer of offers) {
    if (offer.plan_id === planId) {
      for (const t of offer.allowed_term_months) terms.add(t);
    }
  }
  if (terms.size > 0) return [...terms].sort((a, b) => a - b);
  // Fall back to default terms (1 month only shown when an offer explicitly targets it)
  return [3, 6, 12];
}

/**
 * Returns the pre-selected term for a member on a specific plan, if any.
 */
export function getPreselectedTermForMemberPlan(
  offers: MemberPlanOffer[],
  planId: string | null,
): number | null {
  if (!planId) return null;
  for (const offer of offers) {
    if (offer.plan_id === planId && offer.preselected_term_months != null) {
      return offer.preselected_term_months;
    }
  }
  return null;
}
